import { useEffect } from 'preact/hooks'

interface Props {
  /** Object URL or remote URL to the document. */
  src: string
  /** MIME type — picks the renderer (PDF iframe vs. text iframe vs. download link). */
  mimeType: string
  /** Display name shown in the header. */
  name?: string
  onClose: () => void
}

/**
 * Fullscreen viewer for non-image attachments. Mirrors the
 * `FullscreenImageViewer` ergonomics: clicking the backdrop or pressing Esc
 * closes the overlay; the inner content swallows clicks. PDFs are embedded
 * via the browser's native plugin; HTML/text documents are rendered in a
 * sandboxed iframe so a malformed/foreign document can't navigate the host
 * window. Anything else falls through to a "Download" button.
 */
export function FullscreenDocumentViewer({ src, mimeType, name, onClose }: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const isPdf = mimeType === 'application/pdf'
  const isText = mimeType.startsWith('text/') || /\.(md|markdown|txt|html?)$/i.test(name || '')

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.9)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          width: '100%',
          height: '100%',
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 14px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-surface)',
          flexShrink: 0,
        }}>
          <span style={{ fontSize: 16 }}>{isPdf ? '\u{1F4C4}' : isText ? '\u{1F4DD}' : '\u{1F4CE}'}</span>
          <span style={{
            flex: 1,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {name || 'Document'}
          </span>
          <a
            href={src}
            download={name || 'document'}
            class="btn btn-sm"
            style={{ textDecoration: 'none' }}
          >
            Download
          </a>
          <button class="btn btn-sm" onClick={onClose} title="Close (Esc)">{'\u{00D7}'}</button>
        </div>

        <div style={{ flex: 1, minHeight: 0, background: '#fff' }}>
          {isPdf || isText ? (
            <iframe
              src={src}
              title={name || 'Document'}
              sandbox="allow-same-origin"
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              padding: 32,
              flexDirection: 'column',
              gap: 12,
              color: 'var(--text-muted)',
            }}>
              <span style={{ fontSize: 48 }}>{'\u{1F4CE}'}</span>
              <div style={{ fontSize: 14 }}>This file type can't be previewed in the browser.</div>
              <a
                href={src}
                download={name || 'document'}
                class="btn btn-primary"
                style={{ textDecoration: 'none' }}
              >
                Download
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
